/**
 * F-170 B3.6 — client-side month calendar with booking markers.
 *
 * Pulls `/orders/me/upcoming/` once on mount, groups orders by date, and
 * paints a compact month grid where every day that hosts a booking gets
 * a dot. Tapping a date pops out the order list for that day below the
 * grid. Designed to be cheap: no working-hours, no slot picking — the
 * specialist-side calendar (`MyCalendarPage`) already covers authoring.
 */
import { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Layout } from '@/components/layout';
import { Button, Card, EmptyState, Spinner } from '@/components/atoms';
import { OrderCard } from '@/components/domain';
import { useBackButton } from '@/hooks';
import { orders as ordersApi } from '@/api';
import type { Order } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

function ymdKey(d: Dayjs): string {
  return d.format('YYYY-MM-DD');
}

export function MyClientCalendarPage(): JSX.Element {
  useBackButton();
  const [month, setMonth] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ordersApi.fetchUpcoming();
        if (!cancelled) setOrders(res.results);
      } catch (err) {
        logger.warn('client calendar fetch failed', err);
        if (!cancelled) setOrders([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Group orders by YYYY-MM-DD. Orders without a `scheduled_at`
  // never land on the grid — they're discrete (no specific date).
  const byDay = useMemo(() => {
    const map = new Map<string, Order[]>();
    for (const o of orders ?? []) {
      if (!o.scheduled_at) continue;
      const key = ymdKey(dayjs(o.scheduled_at));
      const bucket = map.get(key);
      if (bucket) bucket.push(o);
      else map.set(key, [o]);
    }
    return map;
  }, [orders]);

  // Build a 6-row grid covering the month + leading/trailing days from
  // neighbouring months so the grid stays a stable rectangle.
  const grid = useMemo(() => {
    const start = month.startOf('month');
    // ISO week: dayjs week starts Sunday → shift to Monday.
    const offset = (start.day() + 6) % 7;
    const gridStart = start.subtract(offset, 'day');
    return Array.from({ length: 42 }, (_, i) => gridStart.add(i, 'day'));
  }, [month]);

  const ordersForSelected = selected ? byDay.get(selected) ?? [] : [];

  return (
    <Layout title="Графік">
      <Card>
        <div className={styles.formRow}>
          <Button variant="secondary" onClick={() => setMonth(month.subtract(1, 'month'))}>
            ←
          </Button>
          <strong style={{ flex: 1, textAlign: 'center' }}>
            {month.format('MMMM YYYY')}
          </strong>
          <Button variant="secondary" onClick={() => setMonth(month.add(1, 'month'))}>
            →
          </Button>
        </div>
      </Card>

      <Card>
        {orders === null ? (
          <Spinner center />
        ) : (
          <>
            <div className={styles.monthHead}>
              {WEEKDAY_LABELS.map((l) => (
                <div key={l} className={styles.monthHeadCell}>
                  {l}
                </div>
              ))}
            </div>
            <div className={styles.monthGrid}>
              {grid.map((d) => {
                const key = ymdKey(d);
                const inMonth = d.month() === month.month();
                const isToday = d.isSame(dayjs(), 'day');
                const isSelected = selected === key;
                const hasOrders = byDay.has(key);
                return (
                  <button
                    key={key}
                    type="button"
                    className={[
                      styles.monthCell,
                      !inMonth ? styles.monthCellOff : '',
                      isToday ? styles.monthCellToday : '',
                      isSelected ? styles.monthCellSelected : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setSelected(isSelected ? null : key)}
                  >
                    <span>{d.date()}</span>
                    {hasOrders ? <span className={styles.monthDot} /> : null}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </Card>

      {selected ? (
        <div className={styles.list}>
          <h3 style={{ margin: '4px 0', fontSize: 14, color: 'var(--fg-muted)' }}>
            {dayjs(selected).format('D MMMM')}
          </h3>
          {ordersForSelected.length === 0 ? (
            <EmptyState
              icon="calendar"
              message="На цей день записів немає."
            />
          ) : (
            ordersForSelected.map((o) => <OrderCard key={o.id} order={o} />)
          )}
        </div>
      ) : null}
    </Layout>
  );
}
